import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  FaArrowRight,
  FaBolt,
  FaKeyboard,
  FaMagic,
  FaSearch,
  FaShieldAlt,
} from "react-icons/fa";
import { shortcutItems } from "../utils/shortcuts";

const SECTION_META = {
  Global: {
    icon: "🌐",
    title: "Global Navigation",
    desc: "Shortcuts that work across all major screens.",
  },
  "Order / POS": {
    icon: "🧾",
    title: "Order Flow",
    desc: "Speed up billing, KOT, customer and payment actions.",
  },
  Tables: {
    icon: "🪑",
    title: "Table Operations",
    desc: "Quick controls for table lists, refresh and takeaway management.",
  },
  "Searchable Lists": {
    icon: "🔎",
    title: "Search-first Screens",
    desc: "Quickly find items on reports and shortcut pages.",
  },
  "Profile / Settings": {
    icon: "⚙️",
    title: "Account & Settings",
    desc: "Save and edit actions for profile and printer settings.",
  },
};

const BEHAVIOR_CARDS = [
  {
    icon: <FaShieldAlt />,
    title: "Browser-safe",
    text: "Native browser shortcuts such as Ctrl/Cmd+F and Ctrl/Cmd+N are never intercepted.",
  },
  {
    icon: <FaMagic />,
    title: "Input-safe",
    text: "Shortcuts pause while you type in an input, textarea or select. Esc remains active for closing panels.",
  },
  {
    icon: <FaBolt />,
    title: "Context-aware",
    text: "Order shortcuts work only on the order screen, while table shortcuts run only on the tables page.",
  },
];

const routeLabel = (patterns = []) => {
  const first = Array.isArray(patterns) ? patterns[0] : patterns;
  switch (first) {
    case "*":
      return "All screens";
    case "/order/*":
      return "Order page";
    case "/tables":
      return "Tables page";
    case "/reports":
      return "Reports hub";
    case "/more":
      return "Quick access page";
    case "/profile":
      return "Profile page";
    case "/printer-settings":
      return "Printer settings";
    default:
      return String(first || "Scoped");
  }
};

const KeyboardShortcutsPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const allSections = useMemo(() => {
    const grouped = shortcutItems.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([section, items]) => ({
      section,
      items,
    }));
  }, []);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSections;

    return allSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const hay = [
            item.combo,
            item.label,
            item.section,
            routeLabel(item.routeMatch),
          ]
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [allSections, query]);

  const stats = useMemo(() => {
    const globalCount = shortcutItems.filter((item) => item.scope === "global").length;
    const scopedCount = shortcutItems.length - globalCount;
    return {
      total: shortcutItems.length,
      globalCount,
      scopedCount,
      sections: allSections.length,
    };
  }, [allSections]);

  return (
    <Page>
      <Hero>
        <HeroCopy>
          <Eyebrow>Keyboard Guide</Eyebrow>
          <Title>Master every keyboard shortcut in one place</Title>
          <Sub>
            Explore the app’s live shortcut system. This list comes directly from the shared
            registry, so the guide always stays in sync with actual behavior.
          </Sub>
          <StatRow>
            <StatCard>
              <strong>{stats.total}</strong>
              <span>Total Shortcuts</span>
            </StatCard>
            <StatCard>
              <strong>{stats.globalCount}</strong>
              <span>Global</span>
            </StatCard>
            <StatCard>
              <strong>{stats.scopedCount}</strong>
              <span>Page-specific</span>
            </StatCard>
            <StatCard>
              <strong>{stats.sections}</strong>
              <span>Shortcut Zones</span>
            </StatCard>
          </StatRow>
        </HeroCopy>

        <HeroPanel>
          <SearchWrap>
            <FaSearch />
            <input
              data-shortcut-search="page"
              type="text"
              placeholder="Search combo, action or screen... e.g. Alt+K"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </SearchWrap>
          <TipBox>
            <strong>Quick start</strong>
            <span>Press `?` to open the shortcut overlay, or `Ctrl/Cmd+K` to focus app search.</span>
          </TipBox>
          <CtaRow>
            <PrimaryBtn type="button" onClick={() => navigate("/tables")}>
              Try on POS <FaArrowRight size={12} />
            </PrimaryBtn>
            <GhostBtn type="button" onClick={() => navigate("/order/TakeAway-1")}>
              Open Quick Order
            </GhostBtn>
          </CtaRow>
        </HeroPanel>
      </Hero>

      <BehaviorGrid>
        {BEHAVIOR_CARDS.map((card) => (
          <BehaviorCard key={card.title}>
            <BehaviorIcon>{card.icon}</BehaviorIcon>
            <div>
              <BehaviorTitle>{card.title}</BehaviorTitle>
              <BehaviorText>{card.text}</BehaviorText>
            </div>
          </BehaviorCard>
        ))}
      </BehaviorGrid>

      <SectionGrid>
        {filteredSections.map(({ section, items }) => {
          const meta = SECTION_META[section] || {
            icon: "⌨️",
            title: section,
            desc: "Shortcut section",
          };
          return (
            <ShortcutSection key={section}>
              <SectionHead>
                <SectionBadge>{meta.icon}</SectionBadge>
                <div>
                  <SectionTitle>{meta.title}</SectionTitle>
                  <SectionDesc>{meta.desc}</SectionDesc>
                </div>
              </SectionHead>

              <ShortcutList>
                {items.map((item) => (
                  <ShortcutRow key={item.id}>
                    <ShortcutMeta>
                      <ShortcutLabel>{item.label}</ShortcutLabel>
                      <ScopeChip>{routeLabel(item.routeMatch)}</ScopeChip>
                    </ShortcutMeta>
                    <ShortcutCombo>{item.combo}</ShortcutCombo>
                  </ShortcutRow>
                ))}
              </ShortcutList>
            </ShortcutSection>
          );
        })}
      </SectionGrid>

      {filteredSections.length === 0 ? (
        <EmptyState>
          <FaKeyboard size={18} />
          <span>No shortcuts matched this search.</span>
        </EmptyState>
      ) : null}
    </Page>
  );
};

export default KeyboardShortcutsPage;

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(12px, 1.5vw, 20px);
  background:
    radial-gradient(circle at 0% 0%, rgba(56, 189, 248, 0.2), transparent 28%),
    radial-gradient(circle at 100% 0%, rgba(14, 165, 233, 0.16), transparent 30%),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
  display: grid;
  gap: 12px;
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.95fr);
  gap: 16px;
  padding: clamp(18px, 2vw, 24px);
  border-radius: 20px;
  background:
    radial-gradient(circle at top right, rgba(34, 197, 94, 0.18), transparent 28%),
    linear-gradient(135deg, #082f49 0%, #0f172a 46%, #111827 100%);
  color: #e2e8f0;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.32);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  display: grid;
  gap: 9px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(186, 230, 253, 0.12);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.55rem, 2.6vw, 2.25rem);
  line-height: 1.12;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin: 0;
  max-width: 720px;
  color: rgba(226, 232, 240, 0.86);
  line-height: 1.5;
  font-size: 0.92rem;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  display: grid;
  gap: 4px;
  padding: 9px 12px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.16);

  strong {
    font-size: 1.45rem;
    color: #f8fafc;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: rgba(226, 232, 240, 0.82);
  }
`;

const HeroPanel = styled.div`
  display: grid;
  align-content: start;
  gap: 10px;
  padding: 13px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.16);
`;

const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 11px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #0f172a;

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #0f172a;
  }
`;

const TipBox = styled.div`
  display: grid;
  gap: 6px;
  padding: 11px 12px;
  border-radius: 12px;
  background: rgba(125, 211, 252, 0.12);
  color: var(--app-primary-soft);

  strong {
    color: #f8fafc;
  }

  span {
    line-height: 1.5;
    font-size: 13px;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 13px;
  background: #38bdf8;
  color: #082f49;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const GhostBtn = styled.button`
  border-radius: 999px;
  padding: 9px 13px;
  background: transparent;
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.18);
  font-weight: 700;
  cursor: pointer;
`;

const BehaviorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const BehaviorCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 11px;
  padding: 12px 14px;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(191, 219, 254, 0.75);
  box-shadow: 0 12px 30px rgba(148, 163, 184, 0.14);
`;

const BehaviorIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: var(--app-primary-soft);
  color: var(--app-primary);
`;

const BehaviorTitle = styled.h3`
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 1rem;
`;

const BehaviorText = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.42;
  font-size: 0.84rem;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ShortcutSection = styled.section`
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(191, 219, 254, 0.72);
  box-shadow: 0 12px 34px rgba(148, 163, 184, 0.12);
  display: grid;
  gap: 11px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const SectionBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: var(--app-primary-soft);
  font-size: 17px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
`;

const SectionDesc = styled.p`
  margin: 2px 0 0;
  color: #64748b;
  line-height: 1.4;
  font-size: 0.82rem;
`;

const ShortcutList = styled.div`
  display: grid;
  gap: 7px;
`;

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 11px;
  border-radius: 11px;
  background: #f8fbff;
  border: 1px solid var(--app-primary-soft);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ShortcutMeta = styled.div`
  display: grid;
  gap: 4px;
`;

const ShortcutLabel = styled.strong`
  color: #0f172a;
  font-size: 0.88rem;
`;

const ScopeChip = styled.span`
  width: fit-content;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 11px;
  font-weight: 800;
`;

const ShortcutCombo = styled.code`
  padding: 6px 9px;
  border-radius: 999px;
  background: #0f172a;
  color: #f8fafc;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  color: #475569;
  border: 1px solid var(--app-primary-soft);
`;
